import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, Loader2 } from "lucide-react";
import {
  useCleaningPreferences,
  type CleaningPreferences,
} from "@/hooks/useCleaningPreferences";

const TOGGLES: { key: keyof CleaningPreferences; title: string; description: string }[] = [
  {
    key: "remove_duplicates",
    title: "Remove duplicate rows",
    description: "Drop rows that are exact copies of another row",
  },
  {
    key: "handle_missing_values",
    title: "Handle empty cells",
    description: "Fill or flag cells that have no value",
  },
  {
    key: "standardize_formats",
    title: "Standardise formats",
    description: "Turn text like \"₦5,000\" into numbers and dates into YYYY-MM-DD",
  },
  {
    key: "trim_whitespace",
    title: "Remove extra spaces",
    description: "Strip leading and trailing spaces from text",
  },
  {
    key: "normalize_case",
    title: "Consistent capitalisation",
    description: "Title-case text columns so \"lagos\" and \"Lagos\" match",
  },
  {
    key: "remove_outliers",
    title: "Remove unusual values",
    description: "Drop numbers more than 3 standard deviations from the average",
  },
  {
    key: "validate_emails",
    title: "Check email addresses",
    description: "Flag values in email columns that aren't valid addresses",
  },
  {
    key: "validate_phones",
    title: "Check phone numbers",
    description: "Flag values in phone columns that don't look like numbers",
  },
];

export const DataCleaningPreferences = () => {
  const { preferences, isLoading, isSaving, savePreferences } = useCleaningPreferences();

  if (isLoading) {
    return (
      <Card className="p-6 space-y-4">
        <Skeleton className="h-6 w-56" />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Cleaning settings</h2>
        <div className="flex items-center gap-2">
          {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger aria-label="About cleaning settings">
                <Info className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  These settings are saved to your account and control exactly what
                  the cleaner does to your data.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Saved to your account and applied every time you clean a file.
      </p>

      <div className="space-y-5">
        {TOGGLES.map((option) => (
          <div key={option.key} className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor={option.key}>{option.title}</Label>
              <p className="text-sm text-muted-foreground">{option.description}</p>
            </div>
            <Switch
              id={option.key}
              checked={Boolean(preferences[option.key])}
              onCheckedChange={(checked) =>
                savePreferences({ [option.key]: checked } as Partial<CleaningPreferences>)
              }
            />
          </div>
        ))}

        <div className="flex items-center justify-between gap-4 pt-2 border-t">
          <div className="space-y-0.5">
            <Label htmlFor="missing-strategy">What to do with empty cells</Label>
            <p className="text-sm text-muted-foreground">
              Only used when "Handle empty cells" is on
            </p>
          </div>
          <Select
            value={preferences.missing_value_strategy}
            onValueChange={(v) =>
              savePreferences({
                missing_value_strategy: v as CleaningPreferences["missing_value_strategy"],
              })
            }
          >
            <SelectTrigger id="missing-strategy" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="flag">Mark them "Unknown"</SelectItem>
              <SelectItem value="most_common">Use the most common value</SelectItem>
              <SelectItem value="mean">Use the average (numbers)</SelectItem>
              <SelectItem value="drop">Delete the whole row</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
};
